import ListAltIcon from "@mui/icons-material/ListAlt";
import { grey } from "@mui/material/colors";
import { useAlerts } from "components/Alerts/AlertProvider";
import { DynamicFormDialog } from "components/DynamicFormDialog";
import { hmbpSectionDisplay } from "encamp-shared/src/hmbp";
import { TierIiReportCaStep } from "generated-graphql/graphql";
import { PropsWithChildren, useState } from "react";

import {
  useCAStepStatus,
  useGuidance,
  useHmbpDynamicFields,
  useHmbpIssues,
  useHmbpStateFieldMutation,
  useHmbpValidator,
} from "../../useHmbp";
import { useReport } from "../../useReport";
import { useReportIsVerified } from "../../useReportIsVerified";
import { useTouchReportCAStepMutation } from "../../useTouchReportCAStepMutation";
import { BaseRow, SectionProps } from "./common";

export function FormSection({ section, children }: PropsWithChildren<SectionProps>) {
  const [isOpen, setIsOpen] = useState(false);

  const title = hmbpSectionDisplay[section];
  const fields = useHmbpDynamicFields(section);
  const issues = useHmbpIssues(section);
  const validator = useHmbpValidator(section);
  const { mutation } = useHmbpStateFieldMutation();
  const alerts = useAlerts();
  const guidance = useGuidance(section);

  const { data, refetch: refetchReport } = useReport();
  const hmbpSection = section as unknown as TierIiReportCaStep;
  const isVerified = useReportIsVerified();
  const status = useCAStepStatus(hmbpSection);
  const { handleTouch } = useTouchReportCAStepMutation(
    data?.tierIIReport.id ?? "",
    data?.tierIIReport.touchedCASteps ?? [],
    hmbpSection
  );

  if (guidance === "None") {
    return <></>;
  }

  return (
    <>
      <BaseRow
        title={title}
        guidance={guidance}
        icon={<ListAltIcon sx={{ color: grey[400] }} />}
        onClick={() => setIsOpen(true)}
        section={section}
        issues={issues}
        status={status}
      />
      <DynamicFormDialog
        title={title}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        displayOnly={isVerified}
        persistedIssues={issues}
        fields={fields}
        validator={validator}
        onSubmit={async (data) => {
          try {
            await handleTouch();
            await mutation(data);
            setIsOpen(false);
            refetchReport();
            alerts.success("State information saved successfully.");
          } catch (err) {
            alerts.error("Error saving state information.");
          }
        }}
      >
        {children}
      </DynamicFormDialog>
    </>
  );
}
